import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Separator } from "@/components/ui/separator";
import { Building2, MapPin, Users, ArrowRight, Clock, User, Phone, Star } from "lucide-react";
import SEOHead from "@/components/SEOHead";
import api from "@/lib/api";

const HOSPITAL_TYPES = [
  { slug: "govt-hospital", label: "Government Hospital", icon: Building2, color: "bg-indigo-500", desc: "Central and state government healthcare facilities", stat: "1,200+ across India" },
  { slug: "private-hospital", label: "Private Hospital", icon: Building2, color: "bg-purple-500", desc: "Private healthcare sector - owners, doctors, and staff", stat: "43,000+ nationwide" },
  { slug: "district-hospital", label: "District Hospital", icon: MapPin, color: "bg-amber-500", desc: "District level healthcare centers serving rural areas", stat: "760+ districts" },
  { slug: "civil-hospital", label: "Civil Hospital", icon: Building2, color: "bg-teal-500", desc: "Major civil hospitals and their developments", stat: "500+ civil hospitals" },
];

export default function HospitalsPage() {
  const [articlesByType, setArticlesByType] = useState({});
  const [categories, setCategories] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      try {
        const catRes = await api.get("/categories");
        const catMap = {};
        (catRes.data || []).forEach(c => { catMap[c.slug] = c; });
        setCategories(catMap);

        const results = {};
        for (const ht of HOSPITAL_TYPES) {
          const cat = catMap[ht.slug];
          if (cat) {
            const artRes = await api.get(`/articles?category_id=${cat.id}&limit=4`);
            results[ht.slug] = artRes.data.articles || [];
          } else {
            results[ht.slug] = [];
          }
        }
        setArticlesByType(results);
      } catch (e) { console.error(e); }
      setLoading(false);
    };
    load();
  }, []);

  if (loading) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <Skeleton className="h-12 w-64 mb-8" />
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">{[1,2,3,4].map(i => <Skeleton key={i} className="h-80" />)}</div>
      </div>
    );
  }

  return (
    <div data-testid="hospitals-page">
      <SEOHead title="Hospitals" description="Healthcare facility news, updates, and developments across government, private, district, and civil hospitals." type="website" />

      {/* Hero Banner */}
      <div className="bg-slate-900 text-white py-16 lg:py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center gap-3 mb-4">
            <Building2 className="h-5 w-5 text-[#1E88E5]" />
            <span className="text-[10px] font-sans font-bold uppercase tracking-[0.2em] text-[#1E88E5]">Healthcare Facilities</span>
          </div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-serif font-bold leading-tight">Hospitals</h1>
          <p className="text-lg font-sans text-slate-400 mt-4 max-w-2xl">
            News, updates, and developments from healthcare facilities across India &mdash; government, private, district, and civil hospitals.
          </p>
          <div className="flex flex-wrap gap-6 mt-8">
            {HOSPITAL_TYPES.map(ht => (
              <div key={ht.slug} className="flex items-center gap-2 text-sm font-sans text-slate-300">
                <ht.icon className="h-4 w-4 text-[#26A69A]" />
                <span>{ht.stat}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Hospital Type Sections */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 lg:py-16">
        <div className="space-y-16">
          {HOSPITAL_TYPES.map((ht) => {
            const articles = articlesByType[ht.slug] || [];
            return (
              <section key={ht.slug} data-testid={`hospital-section-${ht.slug}`}>
                <div className="flex items-start justify-between mb-6">
                  <div className="flex items-center gap-4">
                    <div className={`w-12 h-12 ${ht.color} flex items-center justify-center`}>
                      <ht.icon className="h-6 w-6 text-white" />
                    </div>
                    <div>
                      <h2 className="text-2xl sm:text-3xl font-serif font-bold text-foreground">{ht.label}</h2>
                      <p className="text-sm font-sans text-muted-foreground mt-1">{ht.desc}</p>
                    </div>
                  </div>
                  <Link to={`/category/${ht.slug}`}>
                    <Button variant="outline" size="sm" className="font-sans text-xs hidden sm:flex" data-testid={`view-all-${ht.slug}`}>
                      View All <ArrowRight className="h-3 w-3 ml-1" />
                    </Button>
                  </Link>
                </div>

                {articles.length > 0 ? (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
                    {articles.map(art => (
                      <Link key={art.id} to={`/article/${art.id}`} className="group border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 hover:-translate-y-1 transition-transform" data-testid={`hospital-article-${art.id}`}>
                        {art.featured_image && (
                          <div className="aspect-[16/10] overflow-hidden">
                            <img src={art.featured_image} alt={art.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" loading="lazy" />
                          </div>
                        )}
                        <div className="p-4">
                          <h3 className="font-serif font-bold text-foreground group-hover:text-[#1E88E5] transition-colors leading-snug line-clamp-2 text-sm">{art.title}</h3>
                          <p className="text-xs font-sans text-muted-foreground mt-2 line-clamp-2">{art.summary}</p>
                          <div className="flex items-center gap-2 mt-3 text-[11px] font-sans text-muted-foreground">
                            {art.author_name && <span className="flex items-center gap-1"><User className="h-3 w-3" />{art.author_name}</span>}
                            {art.reading_time && <span className="flex items-center gap-1"><Clock className="h-3 w-3" />{art.reading_time}</span>}
                          </div>
                        </div>
                      </Link>
                    ))}
                  </div>
                ) : (
                  <div className="border border-dashed border-slate-300 dark:border-slate-600 p-8 text-center">
                    <Building2 className="h-8 w-8 text-slate-300 mx-auto mb-3" />
                    <p className="text-sm font-sans text-muted-foreground">No articles yet in this category</p>
                    <p className="text-xs font-sans text-muted-foreground mt-1">Content coming soon</p>
                  </div>
                )}

                <Separator className="mt-12" />
              </section>
            );
          })}
        </div>
      </div>
    </div>
  );
}
